import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Trophy, Medal, Crown } from "lucide-react";
import { toast } from "sonner";

export default function Leaderboard() {
  const { t } = useI18n();
  const [tab, setTab] = useState("demo"); // demo or real
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchLeaderboard = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/gamification/leaderboard?mode=${tab}`);
        if (active) setLeaders(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchLeaderboard();
    return () => { active = false; };
  }, [tab]);

  const renderIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-orange-400" />;
    return <span className="text-dim font-bold font-mono">{rank}</span>;
  };

  return (
    <div className="min-h-screen bg-app text-white">
      <TopBar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex items-center gap-3 mb-6">
          <Trophy className="w-8 h-8 text-buy" />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("leaderboard_title")}</h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-line">
          <button
            onClick={() => setTab("demo")}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold ${
              tab === "demo" ? "text-white border-b-2 border-buy" : "text-muted hover:text-white"
            }`}
          >
            {t("leaderboard_demo_rank")}
          </button>
          <button
            onClick={() => setTab("real")}
            className={`px-4 py-2 text-xs uppercase tracking-widest font-semibold ${
              tab === "real" ? "text-white border-b-2 border-buy" : "text-muted hover:text-white"
            }`}
          >
            {t("leaderboard_real_rank")}
          </button>
        </div>

        {/* Leaderboard Table */}
        <div className="bg-panel border border-line rounded-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-dim text-sm uppercase tracking-widest">{t("loading")}</div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-app border-b border-line">
                <tr className="text-[10px] text-dim uppercase tracking-widest">
                  <th className="px-4 py-3 w-16 text-center">{t("leaderboard_rank")}</th>
                  <th className="px-4 py-3">{t("leaderboard_user")}</th>
                  <th className="px-4 py-3 text-right">{t("leaderboard_balance")}</th>
                </tr>
              </thead>
              <tbody>
                {leaders.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-dim">{t("leaderboard_no_data")}</td></tr>
                )}
                {leaders.map((u) => (
                  <tr key={u.rank} className="border-b border-subtle last:border-0 hover:bg-panel-hover transition-colors">
                    <td className="px-4 py-4 text-center">
                      <div className="flex justify-center items-center">
                        {renderIcon(u.rank)}
                      </div>
                    </td>
                    <td className="px-4 py-4 font-mono text-sm">{u.display_name}</td>
                    <td className="px-4 py-4 text-right font-mono text-buy font-bold">
                      ${Number(u.balance).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
